import React, {useCallback, useContext, useMemo} from 'react';
import * as Navigation from '@react-navigation/native';
import {DarkTheme as NavDarkTheme, useFocusEffect} from '@react-navigation/native';
import {Provider as PaperProvider, useTheme as usePaperTheme} from 'react-native-paper';
import {ThemeContext} from '../../helpers/ThemeContext';
import {darkTheme} from '../../config/themes';
import {getPaperTheme} from '../../core/theme';
import {usePosLayout} from '../../helpers/posLayout';
import {usePosPreference} from '../../helpers/posPreferences';
import {holdLightStatusBar} from '../../helpers/posStatusBar';
import {buildForcedDarkThemeValue, shouldForcePosDark} from '../../helpers/posTheme';

/** React Navigation 6 exports ThemeProvider; fall back to a pass-through if it ever disappears. */
const NavThemeProvider = Navigation.ThemeProvider || (({children}) => children);

/** React Navigation theme matching the dark POS palette (mirrors AppProviders). */
const POS_NAV_THEME = {
    ...NavDarkTheme,
    dark: true,
    colors: {
        ...NavDarkTheme.colors,
        primary: darkTheme.colors.primary,
        background: darkTheme.colors.background,
        card: darkTheme.colors.card,
        text: darkTheme.colors.text,
        border: darkTheme.colors.border,
        notification: darkTheme.colors.notification || NavDarkTheme.colors.notification,
    },
};

/** Light status-bar glyphs while a forced-dark POS screen is focused. */
const DarkStatusBarWhileFocused = ({active, appIsDark}) => {
    useFocusEffect(useCallback(
        () => (active ? holdLightStatusBar(appIsDark ? 'light-content' : 'dark-content') : undefined),
        [active, appIsDark],
    ));

    return null;
};

/**
 * Re-provides the app theme (ThemeContext, React Navigation, Paper) as the dark
 * navy POS theme for one screen subtree when {@link shouldForcePosDark} holds.
 * Nested tab navigators inside the screen (Sale / Cart / Orders / Functions)
 * inherit it. Otherwise the app's own values are passed through.
 *
 * The provider tree is identical in both states on purpose: switching (rotating
 * a phone past the tablet breakpoint, flipping the setting) only changes context
 * values and never remounts the screen — an open cart must survive it.
 *
 * @param {object} props
 * @param {React.ReactNode} props.children Screen subtree.
 * @returns {JSX.Element}
 */
export const PosThemeScope = ({children}) => {
    const parent = useContext(ThemeContext);
    const parentNavTheme = Navigation.useTheme();
    const parentPaperTheme = usePaperTheme();
    const {isPhoneUi} = usePosLayout();
    const posDarkMode = usePosPreference('posDarkMode');
    const forced = shouldForcePosDark({isPhoneUi, posDarkMode});
    const darkValue = useMemo(() => buildForcedDarkThemeValue(parent), [parent]);
    const darkPaperTheme = useMemo(() => getPaperTheme(true), []);

    return (
        <ThemeContext.Provider value={forced ? darkValue : parent}>
            <NavThemeProvider value={forced ? POS_NAV_THEME : parentNavTheme}>
                <PaperProvider theme={forced ? darkPaperTheme : parentPaperTheme}>
                    <DarkStatusBarWhileFocused active={forced} appIsDark={darkValue.appIsDark} />
                    {children}
                </PaperProvider>
            </NavThemeProvider>
        </ThemeContext.Provider>
    );
};

/**
 * Wraps a POS stack screen in {@link PosThemeScope}. Call at module scope so the
 * wrapped component keeps a stable identity.
 *
 * @param {React.ComponentType} Screen Screen component.
 * @returns {React.ComponentType}
 */
export const withPosTheme = (Screen) => {
    const Scoped = (props) => (
        <PosThemeScope>
            <Screen {...props} />
        </PosThemeScope>
    );

    Scoped.displayName = `withPosTheme(${Screen.displayName || Screen.name || 'Screen'})`;

    return Scoped;
};

export default PosThemeScope;
